export type SectionKind = 'files' | 'text'
export type FileStatus = 'pending' | 'complete' | 'aborted'
export type DropboxStatus = 'open' | 'ready' | 'closed'
export type Phase = 'open' | 'soft_closed' | 'expired'

/**
 * What the recipient endpoint returns. Deliberately narrower than the row: the
 * server sends this exact shape to /d/<token>, so anything the owner alone
 * should know — their address, the webhook target, whether the confirmation
 * went out — is simply absent rather than blanked.
 */
export interface Dropbox {
  id: string
  title: string
  description: string
  maxBytes: number
  softDeadline: string
  hardDeadline: string
  status: DropboxStatus
  readyAt: string | null
  createdAt: string
  updatedAt: string
}

/** The same dropbox as the owner sees it, via /a/<token> or /api/admin. */
export interface AdminDropbox extends Dropbox {
  ownerEmail: string
  /** Who the upload link was mailed to, '' when nobody was invited. */
  recipientEmail: string
  inviteSentAt: string | null
  confirmationSentAt: string | null
  webhookUrl: string | null
}

export interface Section {
  id: string
  position: number
  kind: SectionKind
  title: string
  description: string
  required: boolean
  maxFiles: number | null
  accept: string[]
}

/**
 * The editable half of a Section: everything except id and position, which the
 * server owns. Sent nested inside CreateDropboxBody and, on its own, to
 * POST /api/a/<token>/sections — one declaration, so the builder and the admin
 * editor provably speak the same language.
 */
export interface SectionInput {
  kind: SectionKind
  title: string
  description: string
  required: boolean
  maxFiles: number | null
  accept: string[]
}

/**
 * A section patch. Absent fields are left alone, which is why every one is
 * optional rather than nullable.
 *
 * kind is absent by construction: files already live under a files section and
 * a note under a text one, so flipping it would orphan them. The server refuses
 * the field outright rather than ignoring it.
 *
 * maxFiles is the one field where undefined and null differ — undefined leaves
 * the cap alone, null removes it.
 */
export interface SectionPatchBody {
  title?: string
  description?: string
  required?: boolean
  maxFiles?: number | null
  accept?: string[]
}

export interface StoredFile {
  id: string
  sectionId: string
  filename: string
  sizeBytes: number
  contentType: string
  status: FileStatus
  createdAt: string
  completedAt: string | null
  /** Server-rendered preview; see thumbStatus before pointing an <img> at it. */
  thumbWidth: number | null
  thumbHeight: number | null
  thumbStatus: 'none' | 'pending' | 'rendering' | 'ready' | 'failed'
}

export interface TextEntry {
  sectionId: string
  lexicalJson: unknown
  plainText: string
  updatedAt: string
}

export interface Quota {
  maxBytes: number
  usedBytes: number
  freeBytes: number
}

export interface Readiness {
  ready: boolean
  requiredTotal: number
  requiredFilled: number
  sections: { sectionId: string; required: boolean; satisfied: boolean }[]
  missingSectionIds: string[]
}

export interface DropboxEvent {
  id: number
  actor: 'recipient' | 'admin' | 'system'
  type: string
  payload: Record<string, unknown>
  at: string
}

/**
 * The page payload. Parameterised on the dropbox shape so the admin view can
 * reach the owner-only fields and the recipient view cannot — reading
 * `dropbox.ownerEmail` off a recipient view is a type error, which is the point.
 */
export interface DropboxView<D extends Dropbox = Dropbox> {
  dropbox: D
  sections: Section[]
  files: StoredFile[]
  texts: TextEntry[]
  quota: Quota
  phase: Phase
  readiness: Readiness
  partSize: number
  /** Whole-dropbox file cap; 0 means unlimited. */
  maxFiles: number
  isAdmin: boolean
  links?: { recipientUrl: string; adminUrl: string }
  events?: DropboxEvent[]
}

export type AdminDropboxView = DropboxView<AdminDropbox>

export interface PartURL {
  partNumber: number
  url: string
}

export interface BeginUploadResponse {
  fileId: string
  uploadId: string
  partSize: number
  partCount: number
  parts: PartURL[]
  quota: Quota
}

/** Server errors carry a stable `code` so the UI can react without parsing prose. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  })

  if (!res.ok) {
    let code = `http_${res.status}`
    let message = res.statusText
    let details: unknown
    try {
      const body = await res.json()
      if (body?.error) {
        code = body.error.code ?? code
        message = body.error.message ?? message
        details = body.error.details
      }
    } catch {
      /* non-JSON error body; keep the status text */
    }
    throw new ApiError(res.status, code, message, details)
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/** Recipient-facing calls, all scoped to the token in the URL. */
export const recipientApi = {
  view: (token: string) => request<DropboxView>(`/api/d/${token}/`),

  saveText: (token: string, sectionId: string, lexicalJson: unknown, plainText: string) =>
    request<{ text: TextEntry }>(`/api/d/${token}/sections/${sectionId}/text`, {
      method: 'PUT',
      body: JSON.stringify({ lexicalJson, plainText }),
    }),

  beginUpload: (
    token: string,
    sectionId: string,
    file: { filename: string; sizeBytes: number; contentType: string },
  ) =>
    request<BeginUploadResponse>(`/api/d/${token}/sections/${sectionId}/uploads`, {
      method: 'POST',
      body: JSON.stringify(file),
    }),

  partUrls: (token: string, fileId: string, from: number, to: number) =>
    request<{ parts: PartURL[]; partCount: number; uploadedParts: { partNumber: number; etag: string }[] }>(
      `/api/d/${token}/uploads/${fileId}/parts?from=${from}&to=${to}`,
    ),

  completeUpload: (
    token: string,
    fileId: string,
    parts: { partNumber: number; etag: string }[],
  ) =>
    request<{ file: StoredFile; quota: Quota }>(`/api/d/${token}/uploads/${fileId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ parts }),
    }),

  abortUpload: (token: string, fileId: string) =>
    request<{ quota: Quota }>(`/api/d/${token}/uploads/${fileId}`, { method: 'DELETE' }),

  ready: (token: string) =>
    request<{ dropbox: Dropbox; readiness: Readiness; confirmationDelay: string }>(
      `/api/d/${token}/ready`,
      { method: 'POST' },
    ),

  unready: (token: string) =>
    request<{ dropbox: Dropbox }>(`/api/d/${token}/ready`, { method: 'DELETE' }),

  downloadUrl: (token: string, fileId: string) => `/api/d/${token}/files/${fileId}/download`,
  thumbUrl: (token: string, fileId: string) => `/api/d/${token}/files/${fileId}/thumb`,
}

/** Admin-link calls. The token never expires and ignores both deadlines. */
export const adminApi = {
  view: (token: string) => request<AdminDropboxView>(`/api/a/${token}/`),

  reopen: (token: string) =>
    request<{ dropbox: AdminDropbox; phase: Phase }>(`/api/a/${token}/reopen`, { method: 'POST' }),

  downloadUrl: (token: string, fileId: string) => `/api/a/${token}/files/${fileId}/download`,
  thumbUrl: (token: string, fileId: string) => `/api/a/${token}/files/${fileId}/thumb`,
  downloadAllUrl: (token: string) => `/api/a/${token}/download-all`,

  /**
   * Irreversible. Removes every stored object, then the dropbox and everything
   * hanging off it. `confirmId` must be the dropbox's own id — the server
   * enforces that too, so the typed confirmation is not merely a UI speed bump.
   */
  destroy: (token: string, confirmId: string) =>
    request<{ deleted: boolean; objectsRemoved: number; uploadsAborted: number }>(
      `/api/a/${token}/`,
      { method: 'DELETE', body: JSON.stringify({ confirmId }) },
    ),

  // Section CRUD. Every one of these answers with the dropbox's complete
  // section list in position order, not only the row that changed: create
  // appends and delete leaves a gap, so a client given one row would have to
  // guess how the rest renumbered.

  /** Appends at the end. 409 section_limit_reached past MAX_SECTIONS_PER_DROPBOX. */
  createSection: (token: string, body: SectionInput) =>
    request<{ section: Section; sections: Section[] }>(`/api/a/${token}/sections`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateSection: (token: string, sectionId: string, body: SectionPatchBody) =>
    request<{ section: Section; sections: Section[] }>(
      `/api/a/${token}/sections/${sectionId}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),

  /**
   * Irreversible. Takes every file uploaded into the section and any note
   * written in it. `confirmId` is the section's short id and the server checks
   * it too, so the typed confirmation is not merely a UI speed bump. 409
   * last_section when it is the only one left.
   */
  deleteSection: (token: string, sectionId: string, confirmId: string) =>
    request<{
      deleted: boolean
      objectsRemoved: number
      uploadsAborted: number
      sections: Section[]
    }>(`/api/a/${token}/sections/${sectionId}`, {
      method: 'DELETE',
      body: JSON.stringify({ confirmId }),
    }),

  /**
   * The whole order, not a delta. A swap cannot be expressed one section at a
   * time, and two concurrent "move to position 2" calls interleave into an
   * order neither caller asked for. 422 stale_order if the ids are not exactly
   * this dropbox's set.
   */
  reorderSections: (token: string, sectionIds: string[]) =>
    request<{ sections: Section[] }>(`/api/a/${token}/sections/order`, {
      method: 'PUT',
      body: JSON.stringify({ sectionIds }),
    }),
}

/** Storage picture across every dropbox, for the dashboard. */
export interface Totals {
  dropboxes: number
  files: number
  usedBytes: number
  thumbnails: number
  allocatedBytes: number
  readyDropboxes: number
  expiredDropboxes: number
}

export interface CreateDropboxBody {
  title: string
  description: string
  ownerEmail: string
  /** Optional. Set it and the server mails the upload link there. */
  recipientEmail?: string
  maxSizeMb: number
  softDeadline: string
  hardDeadline: string
  sections: SectionInput[]
}

/** Owner-facing management. Protected only by whatever proxy sits in front. */
export const managementApi = {
  create: (body: CreateDropboxBody) =>
    request<{ dropbox: AdminDropbox; sections: Section[]; links: { recipientUrl: string; adminUrl: string } }>(
      '/api/admin/dropboxes',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  list: () =>
    request<{
      dropboxes: {
        dropbox: AdminDropbox
        quota: Quota
        phase: Phase
        links: { recipientUrl: string; adminUrl: string }
      }[]
      totals: Totals
    }>('/api/admin/dropboxes'),
}
